import React, { useEffect, useState, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer } from 'recharts';

// 🛰️ GNSS / Galileo — a gnss_telemetry kollekció mérési rekordjai (EU Galileo pályázat)
const card = { background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 12, padding: 16 };
const title = { fontSize: 13, color: '#8b8fa3', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 };
const inp = { background: '#0a0d14', border: '1px solid #2a2e3a', borderRadius: 6, padding: '8px 10px', color: '#e4e6eb', fontSize: 13, boxSizing: 'border-box' };
const btn = { padding: '8px 16px', borderRadius: 6, border: 'none', background: '#00d4ff', color: '#0a0d14', cursor: 'pointer', fontWeight: 600, fontSize: 12 };
const btnGhost = { ...btn, background: 'transparent', border: '1px solid #2a2e3a', color: '#8b8fa3' };

const TRAVEL_LABELS = {
  standing: '🧍 álló', walking: '🚶 gyalog', driving: '🚗 autó', cycling: '🚲 bicikli',
};
const travelLabel = (m) => (m ? TRAVEL_LABELS[m] || m : '—');

const shortUid = (u) => (u && u.length > 11 ? u.slice(0, 8) + '…' + u.slice(-3) : u || '—');
const fmtDt = (ms) => {
  if (!ms) return '—';
  try { return new Date(ms).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'medium' }); }
  catch (e) { return '—'; }
};
// A null ("n/a") NEM azonos a false-szal — a készülék nem adott vivőfrekvenciát.
const dualLabel = (v) => (v === true ? 'igen' : v === false ? 'nem' : 'n/a');
const dualColor = (v) => (v === true ? '#22c55e' : v === false ? '#8b8fa3' : '#f59e0b');
const nn = (v, unit) => (v === null || v === undefined ? '—' : v + (unit || ''));
const pct = (v) => (v === null || v === undefined ? '—' : v + ' %');

function Kpi({ label, value, sub, color }) {
  return (
    <div style={{ ...card, minWidth: 170, flex: 1 }}>
      <div style={title}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || '#e4e6eb' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#8b8fa3', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

const th = { padding: '7px 8px', textAlign: 'left', color: '#8b8fa3', fontWeight: 600, whiteSpace: 'nowrap' };
const td = { padding: '7px 8px', color: '#e4e6eb', whiteSpace: 'nowrap' };

export default function Gnss() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [travelMode, setTravelMode] = useState('');
  const [deviceModel, setDeviceModel] = useState('');
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState(null);

  const qs = useCallback((extra) => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (travelMode) p.set('travelMode', travelMode);
    if (deviceModel) p.set('deviceModel', deviceModel);
    if (type) p.set('type', type);
    Object.entries(extra || {}).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  }, [from, to, travelMode, deviceModel, type]);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    fetch(`/api/gnss/overview?${qs({ page, pageSize })}`, { credentials: 'include' })
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.error || 'HTTP ' + r.status); return j; })
      .then((j) => setData(j))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [qs, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  // Szűrő-váltásnál vissza az első oldalra
  useEffect(() => { setPage(1); }, [from, to, travelMode, deviceModel, type]);

  const download = () => {
    setExporting(true); setExportMsg(null);
    fetch(`/api/gnss/export?${qs()}`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) {
          let msg = 'HTTP ' + r.status;
          try { const j = await r.json(); if (j.error) msg = j.error; } catch (e) { /* nem JSON */ }
          throw new Error(msg);
        }
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        a.href = url; a.download = `gnss_export_${stamp}.xlsx`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setExportMsg({ ok: true, text: 'Letöltés elindult.' });
      })
      .catch((e) => setExportMsg({ ok: false, text: e.message }))
      .finally(() => setExporting(false));
  };

  const resetFilters = () => { setFrom(''); setTo(''); setTravelMode(''); setDeviceModel(''); setType(''); };

  const k = data?.kpis;
  const opts = data?.options || { travelModes: [], deviceModels: [], types: [] };
  const items = data?.items || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h2 style={{ margin: 0, color: '#e4e6eb', fontSize: 20 }}>🛰️ GNSS / Galileo</h2>

      {/* Szűrők */}
      <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <label style={{ fontSize: 12, color: '#8b8fa3' }}>Ettől:</label>
        <input style={inp} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <label style={{ fontSize: 12, color: '#8b8fa3' }}>Eddig:</label>
        <input style={inp} type="date" value={to} onChange={(e) => setTo(e.target.value)} />

        <select style={inp} value={travelMode} onChange={(e) => setTravelMode(e.target.value)}>
          <option value="">Minden mód</option>
          {opts.travelModes.map((m) => <option key={m} value={m}>{travelLabel(m)}</option>)}
        </select>

        <select style={inp} value={deviceModel} onChange={(e) => setDeviceModel(e.target.value)}>
          <option value="">Minden készülék</option>
          {opts.deviceModels.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>

        <select style={inp} value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">Minden típus</option>
          {opts.types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>

        <button style={btnGhost} onClick={resetFilters}>Szűrők törlése</button>
        <button style={btn} onClick={download} disabled={exporting || !data?.total}>
          {exporting ? 'Készítés…' : '📥 Excel export'}
        </button>
        {loading && <span style={{ color: '#8b8fa3', fontSize: 12 }}>Betöltés…</span>}
      </div>

      {error && <div style={{ ...card, borderColor: '#f43f5e', color: '#f43f5e', fontSize: 13 }}>Hiba: {error}</div>}
      {exportMsg && (
        <div style={{ ...card, borderColor: exportMsg.ok ? '#22c55e' : '#f43f5e', color: exportMsg.ok ? '#22c55e' : '#f43f5e', fontSize: 13 }}>
          {exportMsg.ok ? '✅ ' : '⚠️ '}{exportMsg.text}
        </div>
      )}
      {data?.truncated && (
        <div style={{ ...card, borderColor: '#f59e0b', color: '#f59e0b', fontSize: 13 }}>
          ⚠️ A beolvasás elérte a rekord-plafont — szűkíts a dátumtartományon a teljes képhez.
        </div>
      )}

      {/* Üres állapot */}
      {!loading && data && data.total === 0 && (
        <div style={{ ...card, color: '#8b8fa3', fontSize: 14 }}>
          Nincs GNSS-rekord a megadott szűrésre. Az app minden Android POI-triggernél ír egy rekordot —
          ha épp most vezettétek be, várj néhány triggerre, vagy tágíts a dátumtartományon.
        </div>
      )}

      {/* KPI-k */}
      {k && data.total > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <Kpi label="Rekordszám" value={k.records} color="#00d4ff"
            sub={k.recordsWithoutGnss ? `${k.recordsWithoutGnss} GNSS-adat nélkül` : 'mind GNSS-adattal'} />
          <Kpi label="Accuracy medián" value={nn(k.accuracyMedian, ' m')} color="#22c55e"
            sub={`p95: ${nn(k.accuracyP95, ' m')}`} />
          <Kpi label="🛰️ Galileo-kihasználtság" value={pct(k.galileoUtilAvg)} color="#22d3ee"
            sub={`used / visible · összevont: ${pct(k.galileoUtilPooled)} (${k.galileoUsedSum}/${k.galileoVisibleSum})`} />
          <Kpi label="Galileo-részarány" value={pct(k.galileoShareAvg)} color="#a855f7"
            sub="átlagos galileoUsed / usedTotal" />
          <Kpi label="Galileo-fix arány" value={pct(k.galileoFixRatio)} color="#fb923c"
            sub={`${k.galileoFixCount} / ${k.galileoFixBase} fix, ahol galileoUsed > 0`} />
          <Kpi label="Trigger táv medián" value={nn(k.triggerDistanceMedian, ' m')} color="#00d4ff" />
          <Kpi label="Készülék-modellek" value={k.deviceModels} color="#e4e6eb"
            sub={`ebből dualFreq-képes: ${k.dualFreqModels}`} />
        </div>
      )}

      {/* A két Galileo-mutató elkülönítése — könnyű összekeverni őket */}
      {k && data.total > 0 && (
        <div style={{ ...card, fontSize: 12, color: '#8b8fa3', lineHeight: 1.6 }}>
          <b style={{ color: '#22d3ee' }}>Galileo-kihasználtság</b> = <code>galileoUsed / galileoVisible</code> —
          mennyit hasznosít a vevő a <b style={{ color: '#e4e6eb' }}>rendelkezésre álló</b> Galileo-jelből.
          Csak azokat a fixeket veszi számba, ahol legalább egy Galileo-műhold látszott
          ({k.galileoUtilBase} rekord) — nem lehet kihasználatlanságnak minősíteni azt, ami nem is látszik.
          A csempén a <b style={{ color: '#e4e6eb' }}>rekordonkénti átlag</b> áll; az <b style={{ color: '#e4e6eb' }}>összevont</b> érték
          (összes használt / összes látható) a több műholdat látó fixeket nagyobb súllyal veszi.
          <div style={{ marginTop: 4 }}>
            <b style={{ color: '#a855f7' }}>Galileo-részarány</b> = <code>galileoUsed / usedTotal</code> — ettől külön mutató:
            a Galileo <b style={{ color: '#e4e6eb' }}>részesedése a teljes fixben</b>, a többi rendszer mellett.
          </div>
          {k.galileoUtilAnomalies > 0 && (
            <div style={{ color: '#f59e0b', marginTop: 6 }}>
              ⚠️ {k.galileoUtilAnomalies} rekordban a használt Galileo több a láthatónál — ez adathiba a telemetriában,
              érdemes az app oldalán megnézni.
            </div>
          )}
        </div>
      )}

      {/* Napi trend */}
      {data?.daily?.length > 1 && (
        <div style={card}>
          <div style={title}>Napi trend — rekordszám és accuracy-medián</div>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={data.daily.map((d) => ({ ...d, date: d.date.slice(5) }))} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                <CartesianGrid stroke="#2a2e3a" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#8b8fa3" fontSize={11} />
                <YAxis yAxisId="l" stroke="#8b8fa3" fontSize={11} />
                <YAxis yAxisId="r" orientation="right" stroke="#8b8fa3" fontSize={11} unit="m" />
                <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="l" type="monotone" dataKey="count" name="Rekord" stroke="#00d4ff" dot={{ r: 2 }} />
                <Line yAxisId="r" type="monotone" dataKey="accuracyMedian" name="Accuracy medián (m)" stroke="#22c55e" dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Táblázat */}
      {data && data.total > 0 && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <div style={{ ...title, marginBottom: 0 }}>Rekordok — legfrissebb elöl</div>
            <div style={{ fontSize: 12, color: '#8b8fa3' }}>
              {data.total} rekord · {data.page}/{data.totalPages}. oldal
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2a2e3a' }}>
                  <th style={th}>Idő</th>
                  <th style={th}>POI</th>
                  <th style={th}>Táv (m)</th>
                  <th style={th}>Accuracy (m)</th>
                  <th style={th}>Mód</th>
                  <th style={th}>Galileo</th>
                  <th style={th}>C/N0 átlag</th>
                  <th style={th}>dualFreq</th>
                  <th style={th}>Készülék</th>
                  <th style={th}>App</th>
                  <th style={th}>User</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #2a2e3a' }}>
                    <td style={td}>{fmtDt(r.ts)}</td>
                    <td style={{ ...td, whiteSpace: 'normal', maxWidth: 220 }} title={r.poiXid || ''}>{r.poiName || '—'}</td>
                    <td style={td}>{nn(r.triggerDistanceM)}</td>
                    <td style={td}>{nn(r.accuracyM)}</td>
                    <td style={td}>{travelLabel(r.travelMode)}</td>
                    <td style={td}>
                      {r.hasGnss
                        ? <span style={{ color: r.galileoUsed > 0 ? '#22c55e' : '#8b8fa3' }}>
                            {nn(r.galileoUsed)}/{nn(r.usedTotal)}
                          </span>
                        : <span style={{ color: '#f59e0b' }}>nincs GNSS-adat</span>}
                    </td>
                    <td style={td}>{r.galileoCn0Avg === null ? '—' : Math.round(r.galileoCn0Avg * 10) / 10}</td>
                    <td style={{ ...td, color: dualColor(r.hasGnss ? r.dualFreqUsed : null) }}>
                      {dualLabel(r.hasGnss ? r.dualFreqUsed : null)}
                    </td>
                    <td style={td}>{r.deviceModel || '—'}{r.androidVersion ? ` · A${r.androidVersion}` : ''}</td>
                    <td style={td}>{r.appVersion || '—'}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: '#8b8fa3' }} title={r.uid || ''}>{shortUid(r.uid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Lapozás */}
          {data.totalPages > 1 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
              <button style={btnGhost} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page <= 1}>← Előző</button>
              <span style={{ fontSize: 12, color: '#8b8fa3' }}>{data.page} / {data.totalPages}</span>
              <button style={btnGhost} onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))} disabled={data.page >= data.totalPages}>Következő →</button>
            </div>
          )}
        </div>
      )}

      {/* Bontások */}
      {data && data.total > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {[['Utazási mód szerint', data.byTravelMode, true], ['Készülék szerint', data.byDeviceModel, false]].map(([label, list, isMode]) => (
            <div key={label} style={{ ...card, flex: 1, minWidth: 320 }}>
              <div style={title}>{label}</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #2a2e3a' }}>
                    <th style={th}>Csoport</th>
                    <th style={th}>Rekord</th>
                    <th style={th}>Acc. medián</th>
                    <th style={th}>Galileo-fix</th>
                    <th style={{ ...th, color: '#22d3ee' }}>Kihasználtság</th>
                  </tr>
                </thead>
                <tbody>
                  {(list || []).map((g) => (
                    <tr key={g.key} style={{ borderBottom: '1px solid #2a2e3a' }}>
                      <td style={td}>{isMode ? travelLabel(g.key) : g.key}</td>
                      <td style={td}>{g.count}</td>
                      <td style={td}>{nn(g.accuracyMedian, ' m')}</td>
                      <td style={td}>{pct(g.galileoFixRatio)}</td>
                      <td style={{ ...td, color: '#22d3ee', fontWeight: 600 }}
                        title={g.galileoUtilBase
                          ? `összevont: ${pct(g.galileoUtilPooled)} · ${g.galileoUsedSum}/${g.galileoVisibleSum} műhold · ${g.galileoUtilBase} értékelhető rekord`
                          : 'nincs értékelhető rekord (galileoVisible = 0 vagy hiányzik)'}>
                        {pct(g.galileoUtilAvg)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
